import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "react-bootstrap";
import { CalendarSvg } from "../../svgFiles/CalendarSvg";
import ChevronoRightSvg from "../../svgFiles/ChevronoRightSvg";
import CommonModal from "../../components/shared/modalLayout/CommonModal";
import {
  formatDashboardDateShort,
  formatDashboardTime,
  getDashboardDateKey,
} from "../../helpers/dashboardTime";

const CHANGE_LOGS_DEFAULT_WIDTH = 480;
const CHANGE_LOGS_WIDE_WIDTH = 960;
const CHANGE_LOGS_MIN_WIDTH = 360;

const clampChangeLogsWidth = (width) => {
  const max = Math.max(
    CHANGE_LOGS_MIN_WIDTH,
    Math.min(CHANGE_LOGS_WIDE_WIDTH, window.innerWidth - 32)
  );
  return Math.min(max, Math.max(CHANGE_LOGS_MIN_WIDTH, Math.round(width)));
};

const getChangeLogsDialog = () =>
  document.querySelector(".change-logs-modal .modal-dialog");

const ExpandIcon = ({ expanded }) =>
  expanded ? (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M6 2H2v4M10 2h4v4M6 14H2v-4M10 14h4v-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 2l4 4M14 2l-4 4M2 14l4-4M14 14l-4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M6 2H2v4M10 2h4v4M6 14H2v-4M10 14h4v-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 6L2 2M10 6l4-4M6 10L2 14M10 10l4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
const ExpandableText = ({ text, limit = 80 }) => {
  const [expanded, setExpanded] = useState(false);
  const textString = String(text ?? "");

  if (text === null || text === undefined) return <span>N/A</span>;

  const isTooLong = textString.length > limit;
  const displayText = expanded
    ? textString
    : textString.substring(0, limit) + (isTooLong ? "..." : "");

  return (
    <span className="expandable-text-container">
      <span className="expandable-text-content">{displayText}</span>
      {isTooLong && (
        <button
          type="button"
          className="expandable-text-btn"
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </span>
  );
};

const LOG_METHOD_LABELS = {
  OrderFrom: "Order From",
  orderFrom: "Order From",
  customerName: "Customer",
  customerEmail: "Customer Email",
  patientName: "Patient name",
  provider: "Provider name",
  providerName: "Provider name",
  Address1: "Address",
  Address2: "Apartment / Suite",
  ShippingName: "Shipping Name",
  ShippingPhone: "Shipping Phone",
  ShippingCity: "Shipping City",
  ShippingProvince: "Shipping State",
  ShippingZip: "Postal Code",
  TrackingNumber: "Tracking Number",
  ShipDate: "Ship Date",
  ProductType: "Product Type",
  OrderStatus: "Order Status",
  Status: "Order Status",
  isRemoved: "Removed from Shopify",
  softDeleted: "Soft deleted",
  clinicName: "Clinic Name",
  vaClinicName: "Clinic Name",
  hospitalName: "Hospital Name",
  vaHospitalName: "Hospital Name",
  invoicePoNumber: "VA PO Number",
  originalPoNumber: "VA PO Number",
  originalPoFile: "VA PO File",
  originalPoFileKey: "VA PO File",
  originalPoFileName: "VA PO File",
  invoiceFile: "Lovell invoice",
  invoiceFileKey: "Lovell invoice",
  invoiceFileName: "Lovell invoice",
};

const formatReadableLabel = (method) => {
  const raw = String(method ?? "").trim();
  if (!raw) return "Unknown";
  if (LOG_METHOD_LABELS[raw]) return LOG_METHOD_LABELS[raw];

  return raw
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatLogMethod = (method) => {
  const methodName = method ?? "Unknown";

  if (methodName.startsWith("OD(R)_")) {
    const parts = methodName.split("_");
    return (
      <>
        <span className="log-method--od">{parts[0]}</span>_
        {parts.slice(1).join("_")}
      </>
    );
  }

  if (methodName.startsWith("OS(L)_")) {
    const parts = methodName.split("_");
    return (
      <>
        <span className="log-method--os">{parts[0]}</span>_
        {parts.slice(1).join("_")}
      </>
    );
  }

  return formatReadableLabel(methodName);
};

const parseLogDate = (rawDate) => {
  if (!rawDate) return null;
  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const formatLogEntryTime = (rawDate) => {
  // Always Pacific — DB timestamps stay UTC.
  return formatDashboardTime(rawDate, "");
};

const recordTimestamp = (record) => {
  const date = parseLogDate(record?.Date);
  return date ? date.getTime() : 0;
};

const formatActorName = (user) => {
  const raw = String(user ?? "Unknown").trim() || "Unknown";
  return raw.startsWith("By ") ? raw.slice(3) : raw;
};

/** Calendar day in Pacific (YYYY-MM-DD). */
const dateKeyFromRecord = (record) => getDashboardDateKey(record?.Date);

/** Format a Pacific YYYY-MM-DD key as M/D/YYYY. */
const formatLogDayBadge = (dateKey) => {
  if (!dateKey || typeof dateKey !== "string") return "";
  const [y, m, d] = dateKey.split("-").map((part) => Number(part));
  if (!y || !m || !d) return dateKey;
  // Noon UTC avoids DST edge flipping the calendar day when formatting.
  return formatDashboardDateShort(
    new Date(Date.UTC(y, m - 1, d, 12, 0, 0)),
    dateKey
  );
};

const GroupedLogs = ({ groupedRecords, limit }) => {
  const dateEntries = Object.entries(groupedRecords);

  let remaining = Number.isFinite(limit) && limit > 0 ? limit : Infinity;

  return (
    <ul className="logs change-logs">
      {dateEntries.map(([date, recordGroup]) => {
        if (remaining <= 0) return null;

        const sortedLogs = [...recordGroup].sort(
          (a, b) => recordTimestamp(b) - recordTimestamp(a)
        );
        const visibleLogs = sortedLogs.slice(0, remaining);
        remaining -= visibleLogs.length;

        return (
          <li key={date} className="change-logs__day">
            <span className="change-logs__date-badge">
              <CalendarSvg />
              <span className="log-date">{formatLogDayBadge(date)}</span>
            </span>

            <ul className="change-logs__entries">
              {visibleLogs.map((log, index) => {
                const timeLabel = formatLogEntryTime(log?.Date);
                const actor = formatActorName(log?.User);
                const entryKey = `${log?.Date || date}-${log?.Method || "m"}-${index}`;

                return (
                  <li key={entryKey} className="change-logs__entry">
                    <div className="change-logs__entry-main">
                      <p className="log-text change-logs__text">
                        <span className="log-method">
                          {formatLogMethod(log?.Method)}
                        </span>
                        <span className="change-logs__separator">·</span>
                        <ExpandableText text={log?.Data} limit={48} />
                      </p>
                      <div className="change-logs__meta">
                        {timeLabel ? (
                          <time
                            className="log-time-chip"
                            dateTime={
                              log?.Date
                                ? new Date(log.Date).toISOString()
                                : undefined
                            }
                            title={timeLabel}
                          >
                            {timeLabel}
                          </time>
                        ) : null}
                        <span className="change-logs__actor" title={actor}>
                          {actor}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </li>
        );
      })}
    </ul>
  );
};

const UserRecord = ({ RecordData }) => {
  const records = Array.isArray(RecordData?.Record) ? RecordData.Record : [];
  const [showRecordsModal, setShowRecordsModal] = useState(false);
  const [isWideModal, setIsWideModal] = useState(false);
  const [modalWidth, setModalWidth] = useState(CHANGE_LOGS_DEFAULT_WIDTH);
  const [activeResizeSide, setActiveResizeSide] = useState(null);
  const dragRef = useRef(null);

  const userRecords = useMemo(
    () =>
      records.filter(
        (r) =>
          r.Source !== "shopify" &&
          r.Method !== "ShippingName" &&
          r.Method !== "vaShipToRaw"
      ),
    [records]
  );

  const groupedRecords = useMemo(() => {
    const byDate = userRecords.reduce((acc, record) => {
      const dateKey = dateKeyFromRecord(record);
      if (!dateKey) return acc;
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(record);
      return acc;
    }, {});

    return Object.fromEntries(
      Object.entries(byDate).sort(([dateA], [dateB]) =>
        dateA < dateB ? 1 : dateA > dateB ? -1 : 0
      )
    );
  }, [userRecords]);

  const applyDialogWidth = (width) => {
    const dialog = getChangeLogsDialog();
    if (!dialog) return;
    const next = clampChangeLogsWidth(width);
    dialog.style.width = `${next}px`;
    dialog.style.maxWidth = `min(${next}px, calc(100vw - 2rem))`;
  };

  const openRecordsModal = () => {
    setIsWideModal(false);
    setModalWidth(CHANGE_LOGS_DEFAULT_WIDTH);
    setShowRecordsModal(true);
  };

  const closeRecordsModal = () => {
    setShowRecordsModal(false);
    setIsWideModal(false);
    setModalWidth(CHANGE_LOGS_DEFAULT_WIDTH);
    setActiveResizeSide(null);
  };

  const toggleWideModal = () => {
    setIsWideModal((wide) => {
      const next = !wide;
      const width = next ? CHANGE_LOGS_WIDE_WIDTH : CHANGE_LOGS_DEFAULT_WIDTH;
      setModalWidth(width);
      window.requestAnimationFrame(() => applyDialogWidth(width));
      return next;
    });
  };

  useEffect(() => {
    if (!showRecordsModal) return undefined;
    let tries = 0;
    let timer = null;
    const sync = () => {
      const dialog = getChangeLogsDialog();
      if (dialog) {
        applyDialogWidth(modalWidth);
        return;
      }
      if (tries >= 40) return;
      tries += 1;
      timer = window.setTimeout(sync, 50);
    };
    sync();
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [showRecordsModal, modalWidth]);

  useEffect(() => {
    return () => {
      const drag = dragRef.current;
      if (!drag) return;
      window.removeEventListener("pointermove", drag.onMove);
      window.removeEventListener("pointerup", drag.onUp);
      window.removeEventListener("pointercancel", drag.onUp);
      document.body.classList.remove("is-change-logs-resizing");
      dragRef.current = null;
    };
  }, []);

  const startResize = (side) => (event) => {
    event.preventDefault();
    event.stopPropagation();

    const previous = dragRef.current;
    if (previous) {
      window.removeEventListener("pointermove", previous.onMove);
      window.removeEventListener("pointerup", previous.onUp);
      window.removeEventListener("pointercancel", previous.onUp);
    }

    const dialog = getChangeLogsDialog();
    const startX = event.clientX;
    const startWidth = clampChangeLogsWidth(
      dialog?.getBoundingClientRect().width || modalWidth
    );

    setActiveResizeSide(side);
    document.body.classList.add("is-change-logs-resizing");

    const onMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      const nextWidth = clampChangeLogsWidth(
        side === "right" ? startWidth + delta : startWidth - delta
      );
      setModalWidth(nextWidth);
      applyDialogWidth(nextWidth);
      setIsWideModal(nextWidth >= CHANGE_LOGS_WIDE_WIDTH - 8);
    };

    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      document.body.classList.remove("is-change-logs-resizing");
      setActiveResizeSide(null);
      dragRef.current = null;
    };

    dragRef.current = { onMove, onUp };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  return (
    <div className="change-logs-panel">
      <Card className="p-3 mt-3 change-logs-card">
        <h6 className="change-logs-card__title d-flex justify-content-between align-items-center mb-0">
          Change logs
          <span className="d-flex align-items-center gap-2">
            {userRecords.length > 0 && (
              <span className="status-badge log-badge">{userRecords.length}</span>
            )}
            {userRecords.length > 3 && (
              <button
                type="button"
                className="change-logs-card__open"
                onClick={openRecordsModal}
                aria-label="Open all change logs"
              >
                <ChevronoRightSvg />
              </button>
            )}
          </span>
        </h6>

        {userRecords.length === 0 ? (
          <p className="change-logs-empty mb-0 mt-3">No change logs</p>
        ) : (
          <div className="preview-logs-container mt-3">
            <GroupedLogs limit={3} groupedRecords={groupedRecords} />
          </div>
        )}
      </Card>

      <CommonModal
        show={showRecordsModal}
        setShow={setShowRecordsModal}
        onHide={closeRecordsModal}
        className={`${isWideModal ? "modal-lg" : "modal-sm"} change-logs-modal`}
        enforceFocus={false}
        closeOnBackdrop={false}
        title={`Change logs <span class="status-badge log-badge ms-auto me-2">${userRecords.length}</span>`}
        headerActions={
          <button
            type="button"
            className="change-logs-modal__widen"
            onClick={toggleWideModal}
            aria-label={isWideModal ? "Use default width" : "Widen modal"}
            title={isWideModal ? "Default width" : "Widen"}
          >
            <ExpandIcon expanded={isWideModal} />
          </button>
        }
      >
        <div
          className={`change-logs-modal__resize change-logs-modal__resize--left${
            activeResizeSide === "left" ? " is-active" : ""
          }`}
          title="Drag to resize"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize modal from left"
          onPointerDown={startResize("left")}
        />
        <div
          className={`change-logs-modal__resize change-logs-modal__resize--right${
            activeResizeSide === "right" ? " is-active" : ""
          }`}
          title="Drag to resize"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize modal from right"
          onPointerDown={startResize("right")}
        />
        <div className="records-scroll">
          <GroupedLogs groupedRecords={groupedRecords} />
        </div>
      </CommonModal>
    </div>
  );
};

export default UserRecord;
